(() => {
  'use strict';
  if (window.UniAnalytics) return;
  const VERSION = '2026-09-30-v3';
  const STORAGE = 'unisystem_attribution_v2';
  const MAX_AGE = 24 * 60 * 60 * 1000;
  const CAMPAIGN = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'utm_id', 'campaign_id', 'adset_id', 'ad_id'];
  const CLICK = ['gclid', 'gbraid', 'wbraid', 'fbclid', 'ttclid'];
  const EVENTS = {
    view_program: ['ViewContent', 'ViewContent'],
    select_program: ['SelectProgram', 'ClickButton'],
    select_modality: ['', ''],
    click_cta: ['CTA_Click', 'ClickButton'],
    whatsapp_click: ['WhatsApp_Click', 'ClickButton'],
    form_start: ['FormStart', 'ClickButton'],
    form_submit: ['FormSubmit', 'ClickButton'],
    form_success: ['FormSuccess', 'ClickButton'],
    generate_lead: ['Lead', 'SubmitForm']
  };
  const legacy = { view_program: 'ViewContent', select_program: 'SelectProgram', click_cta: 'CTA_Click', whatsapp_click: 'WhatsApp_Click', form_start: 'FormStart', form_submit: 'FormSubmit', form_success: 'FormSuccess', generate_lead: 'Lead' };
  const sent = new Set();
  let loadedGtm = false;
  let consentSignature = '';
  let viewedProgramId;
  let consentOpener;
  let consentSaving = false;
  let memory = {};
  const config = () => document.body?.dataset || {};
  const privacy = () => window.Shopify?.customerPrivacy;
  let privacyReadiness = 'pending';
  let privacyReadyTimeout;
  const privacyReadyCallbacks = [];
  const hasPrivacyApi = () => {
    const api = privacy();
    return typeof api?.currentVisitorConsent === 'function' && typeof api?.analyticsProcessingAllowed === 'function' && typeof api?.marketingAllowed === 'function';
  };
  const whenPrivacyReady = (callback) => {
    if (typeof callback !== 'function') return;
    if (privacyReadiness !== 'pending') { callback(privacyReadiness === 'ready'); return; }
    privacyReadyCallbacks.push(callback);
  };
  const finishPrivacyInitialization = (error) => {
    if (privacyReadiness !== 'pending') return;
    const available = !error && hasPrivacyApi();
    privacyReadiness = available ? 'ready' : 'failed';
    window.clearTimeout(privacyReadyTimeout);
    if (available) ready();
    privacyReadyCallbacks.splice(0).forEach(callback => { try { callback(available); } catch {} });
  };
  const visitorChoices = () => {
    try { return privacy()?.currentVisitorConsent?.() || {}; } catch { return {}; }
  };
  const hasConsentDecision = () => {
    const choices = visitorChoices();
    return ['yes', 'no'].includes(choices.analytics) && ['yes', 'no'].includes(choices.marketing);
  };
  const permission = (purpose) => {
    try {
      const api = privacy();
      const method = purpose === 'marketing' ? 'marketingAllowed' : 'analyticsProcessingAllowed';
      return !window.Shopify?.designMode && visitorChoices()[purpose] === 'yes' && typeof api?.[method] === 'function' && api[method]() === true;
    } catch { return false; }
  };
  const storageGet = (key) => { try { return sessionStorage.getItem(key) || ''; } catch { return ''; } };
  const storageSet = (key, value) => { try { sessionStorage.setItem(key, value); } catch {} };
  const storageRemove = (key) => { try { sessionStorage.removeItem(key); } catch {} };
  const identifier = () => window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2, 14)}`;
  const safeId = (value) => /^[a-zA-Z0-9:_-]{1,120}$/.test(value || '') ? String(value) : '';
  const safeText = (value, key = '') => {
    const text = String(value || '').trim().slice(0, 160).replace(/[\u0000-\u001f\u007f]/g, '');
    if (['campaign_id', 'adset_id', 'ad_id'].includes(key)) return /^[0-9]{1,40}$/.test(text) ? text : '';
    if (/@|%40|(?:\d[\s().+-]*){7,}|https?:\/\//i.test(text)) return '';
    return text;
  };
  const safeClick = (value) => /^[A-Za-z0-9._~-]{1,500}$/.test(value || '') ? String(value) : '';
  const safeURL = (value, originOnly = false) => {
    if (!value) return '';
    try {
      const url = new URL(value, location.origin);
      if (!/^https?:$/.test(url.protocol)) return '';
      if (originOnly) return url.origin;
      let path = url.pathname;
      try { path = decodeURIComponent(path); } catch {}
      if (/@|(?:\d[\s().+-]*){7,}/.test(path)) return url.origin;
      return `${url.origin}${url.pathname}`;
    } catch { return ''; }
  };
  const consent = () => ({ analytics: permission('analytics'), marketing: permission('marketing') });
  function readAttribution() {
    try {
      const result = JSON.parse(storageGet(STORAGE));
      if (['2026-09-19-v2', VERSION].includes(result?.v) && typeof result.created_at === 'number' && Date.now() - result.created_at < MAX_AGE && result.created_at <= Date.now()) return result;
    } catch {}
    storageRemove(STORAGE);
    return {};
  }
  function removeLegacy() {
    [...CAMPAIGN, ...CLICK, 'fbclid_timestamp', 'first_landing_url', 'lead_session_id', 'lead_id'].forEach(key => storageRemove(`unisystem_${key}`));
  }
  function captureAttribution() {
    const api = privacy();
    if (window.Shopify?.designMode || typeof api?.analyticsProcessingAllowed !== 'function' || typeof api?.marketingAllowed !== 'function') { memory = {}; return {}; }
    const allowed = consent();
    const params = new URLSearchParams(location.search);
    memory = readAttribution();
    if (!allowed.analytics && !allowed.marketing) {
      memory = {};
      storageRemove(STORAGE);
      removeLegacy();
      return {};
    }
    memory.v = VERSION;
    memory.created_at ||= Date.now();
    if (allowed.analytics) {
      memory.first_landing_url ||= safeURL(location.href);
      memory.referrer ||= safeURL(document.referrer, true);
      if (CAMPAIGN.some(key => params.has(key))) {
        CAMPAIGN.forEach(key => { memory[key] = safeText(params.get(key), key); });
      }
    } else {
      [...CAMPAIGN, 'first_landing_url', 'referrer'].forEach(key => { delete memory[key]; });
    }
    if (allowed.marketing) {
      if (CLICK.some(key => params.has(key))) {
        const previousFbclid = memory.fbclid;
        CLICK.forEach(key => { memory[key] = safeClick(params.get(key)); });
        if (memory.fbclid && (memory.fbclid !== previousFbclid || !memory.fbclid_timestamp)) memory.fbclid_timestamp = Date.now();
        else if (!memory.fbclid) delete memory.fbclid_timestamp;
      } else if (CAMPAIGN.some(key => params.has(key))) {
        CLICK.forEach(key => { delete memory[key]; });
        delete memory.fbclid_timestamp;
      }
    } else {
      [...CLICK, 'fbclid_timestamp'].forEach(key => { delete memory[key]; });
    }
    storageSet(STORAGE, JSON.stringify(memory));
    removeLegacy();
    return { ...memory };
  }
  const readMarketingCookie = (name) => {
    if (!permission('marketing')) return '';
    const part = document.cookie.split(';').map(item => item.trim()).find(item => item.startsWith(`${name}=`));
    try { return safeClick(decodeURIComponent(part?.slice(name.length + 1) || '')); } catch { return ''; }
  };
  function leadAttribution() {
    const data = captureAttribution();
    const allowed = consent();
    const output = {};
    CAMPAIGN.forEach(key => { output[key] = allowed.analytics ? safeText(data[key], key) : ''; });
    CLICK.forEach(key => { output[key] = allowed.marketing ? safeClick(data[key]) : ''; });
    for (const key of ['source', 'medium', 'campaign', 'content', 'term']) output[key] = output[`utm_${key}`];
    output.first_landing_url = allowed.analytics ? safeURL(data.first_landing_url) : '';
    output.landing_page = output.first_landing_url;
    output.referrer = allowed.analytics ? safeURL(data.referrer, true) : '';
    output.fbp = readMarketingCookie('_fbp');
    output.fbc = readMarketingCookie('_fbc');
    output.ttp = readMarketingCookie('_ttp');
    if (allowed.marketing && !output.fbc && output.fbclid && data.fbclid_timestamp) output.fbc = `fb.1.${data.fbclid_timestamp}.${output.fbclid}`;
    output.analytics_consent = allowed.analytics ? 'granted' : 'denied';
    output.marketing_consent = allowed.marketing ? 'granted' : 'denied';
    output.ad_user_data_consent = output.marketing_consent;
    output.ad_personalization_consent = output.marketing_consent;
    output.attribution_version = VERSION;
    return output;
  }
  function track(name, details = {}, eventId, channelLimit) {
    if (!EVENTS[name]) return false;
    const id = safeId(eventId) || identifier();
    const allowed = consent();
    const analytics = allowed.analytics && (channelLimit === undefined || channelLimit?.analytics === true) && !sent.has(`${name}:${id}:analytics`);
    const marketing = allowed.marketing && (channelLimit === undefined || channelLimit?.marketing === true) && Boolean(EVENTS[name][0] || EVENTS[name][1]) && !sent.has(`${name}:${id}:marketing`);
    if (!analytics && !marketing) return false;
    const data = captureAttribution();
    const program = document.querySelector('uni-program[data-program-id]');
    const programId = safeId(details.program_id || details.programa_id || program?.dataset.programId);
    const requestedMode = details.modalidad || new URLSearchParams(location.search).get('modalidad') || program?.dataset.selectedModality;
    const campaignProgram = ['UNI-005', 'UNI-013', 'UNI-014', 'UNI-017', 'UNI-018'].includes(programId);
    const payload = {
      event: analytics ? name : 'uni_marketing',
      event_id: id,
      tracking_version: VERSION,
      page_path: safeURL(location.href).replace(location.origin, '') || '/',
      page_location: safeURL(location.href),
      page_referrer: safeURL(document.referrer, true),
      program_id: programId,
      programa_id: programId,
      modalidad: campaignProgram ? (programId !== 'UNI-018' && requestedMode === 'presencial' ? 'presencial' : 'distancia') : '',
      periodo: campaignProgram ? '2027-1' : '',
      modalidad_anterior: campaignProgram && ['distancia', 'presencial'].includes(details.modalidad_anterior) ? details.modalidad_anterior : '',
      cta_id: safeId(details.cta_id),
      cta_location: safeId(details.cta_location) || 'content',
      section_id: safeId(details.section_id),
      form_id: safeId(details.form_id),
      form_purpose: safeId(details.form_purpose),
      submission_id: safeId(details.submission_id),
      landing_variant: safeId(details.landing_variant),
      lead_source: safeId(details.lead_source),
      analytics_consent: allowed.analytics ? 'granted' : 'denied',
      marketing_consent: permission('marketing') ? 'granted' : 'denied',
      ad_user_data_consent: permission('marketing') ? 'granted' : 'denied',
      google_event_mode: config().googleEventMode === 'direct_ga4' ? 'direct_ga4' : 'data_layer',
      meta_event_mode: config().metaEventMode === 'existing_pixel' ? 'existing_pixel' : 'data_layer',
      legacy_event_name: legacy[name],
      ga4_event_name: analytics ? name : '',
      meta_event_name: marketing ? EVENTS[name][0] : '',
      tiktok_event_name: marketing ? EVENTS[name][1] : '',
      analytics_dispatch: analytics,
      marketing_dispatch: marketing
    };
    CAMPAIGN.forEach(key => { payload[key] = safeText(data[key], key); });
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(payload);
    if (analytics) sent.add(`${name}:${id}:analytics`);
    if (marketing) sent.add(`${name}:${id}:marketing`);
    const ga4 = config().ga4 || '';
    const gaLoaded = !!window.google_tag_manager?.[ga4];
    if (analytics && payload.google_event_mode === 'direct_ga4' && /^G-[A-Z0-9]+$/.test(ga4) && gaLoaded && typeof window.gtag === 'function') {
      const { event, ...parameters } = payload;
      window.gtag('event', event, { ...parameters, send_to: ga4 });
    }
    const metaId = config().metaPixel || '';
    if (payload.meta_event_mode === 'existing_pixel' && marketing && /^\d+$/.test(metaId) && window.fbq?.loaded === true) {
      const standard = ['view_program', 'generate_lead'].includes(name);
      window.fbq(standard ? 'trackSingle' : 'trackSingleCustom', metaId, EVENTS[name][0], payload, { eventID: id });
    }
    return true;
  }
  function ctaIdentifier(link, card) {
    if (link.dataset.trackWhatsapp) return safeId(`whatsapp_${link.dataset.trackWhatsapp}`);
    if (link.id) return safeId(link.id);
    if (link.hasAttribute('data-program-cta')) return 'program_information';
    if (card) return 'program_card';
    const explicit = link.dataset.track;
    if (explicit && explicit !== 'CTA_Click') return safeId(explicit);
    const label = safeText(link.textContent).normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '').slice(0, 80);
    return safeId(label) || 'link';
  }
  function viewProgram() {
    const program = document.querySelector('uni-program[data-program-id]');
    const english = document.querySelector('[data-uni-contact][data-mode="english"]');
    if (program || english) { viewedProgramId ||= identifier(); track('view_program', { program_id: program?.dataset.programId || 'ingles-cool', cta_location: 'program' }, viewedProgramId); }
  }
  function loadGtm() {
    const id = config().gtm || '';
    if ((!permission('analytics') && !permission('marketing')) || loadedGtm || !/^GTM-[A-Z0-9]+$/.test(id)) return;
    const exists = [...document.scripts].some(script => {
      try { const url = new URL(script.src); return url.hostname === 'www.googletagmanager.com' && url.pathname === '/gtm.js' && url.searchParams.get('id') === id; } catch { return false; }
    });
    if (exists || window.google_tag_manager?.[id]) { loadedGtm = true; return; }
    loadedGtm = true;
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ 'gtm.start': Date.now(), event: 'gtm.js' });
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(id)}`;
    document.head.appendChild(script);
  }
  function syncConsent() {
    const allowed = consent();
    const signature = `${allowed.analytics}:${allowed.marketing}`;
    if (consentSignature !== signature) {
      window.gtag?.('consent', 'update', {
        analytics_storage: allowed.analytics ? 'granted' : 'denied',
        ad_storage: allowed.marketing ? 'granted' : 'denied',
        ad_user_data: allowed.marketing ? 'granted' : 'denied',
        ad_personalization: allowed.marketing ? 'granted' : 'denied'
      });
      consentSignature = signature;
    }
    captureAttribution();
    loadGtm();
    viewProgram();
    document.querySelectorAll('[data-consent-analytics]').forEach(el => { el.checked = allowed.analytics; });
    document.querySelectorAll('[data-consent-marketing]').forEach(el => { el.checked = allowed.marketing; });
  }
  function setConsent(analytics, marketing) {
    const api = privacy();
    const panel = document.querySelector('[data-consent]');
    const status = panel?.querySelector('[data-consent-status]');
    if (consentSaving) return;
    if (typeof api?.setTrackingConsent !== 'function') { if (status) status.hidden = false; return; }
    consentSaving = true;
    const buttons = panel?.querySelectorAll('button') || [];
    buttons.forEach(button => { button.disabled = true; });
    if (status) status.hidden = true;
    let finished = false;
    let timeout;
    const finish = (result) => {
      if (finished) return;
      finished = true;
      window.clearTimeout(timeout);
      consentSaving = false;
      buttons.forEach(button => { button.disabled = false; });
      const choices = visitorChoices();
      const saved = !result?.error && choices.analytics === (analytics ? 'yes' : 'no') && choices.marketing === (marketing ? 'yes' : 'no');
      if (!saved) {
        if (panel) panel.hidden = false;
        if (status) status.hidden = false;
        syncConsent();
        return;
      }
      if (panel) panel.hidden = true;
      if (status) status.hidden = true;
      syncConsent();
      if (consentOpener?.isConnected) consentOpener.focus();
      consentOpener = null;
    };
    timeout = window.setTimeout(() => finish({ error: true }), 8000);
    try { api.setTrackingConsent({ analytics, marketing, preferences: analytics || marketing, sale_of_data: false }, finish); }
    catch { finish({ error: true }); }
  }
  document.addEventListener('click', event => {
    const control = event.target.closest?.('[data-open-consent],[data-consent-accept],[data-consent-reject],[data-consent-save]');
    const panel = document.querySelector('[data-consent]');
    if (control?.matches('[data-open-consent]')) {
      consentOpener = control;
      syncConsent();
      if (panel) {
        panel.hidden = false;
        const preferences = panel.querySelector('.uni-consent__preferences');
        if (preferences) { preferences.hidden = false; preferences.open = true; }
        (preferences?.querySelector('input') || panel.querySelector('button'))?.focus();
      }
      return;
    }
    if (control?.matches('[data-consent-accept]')) { setConsent(true, true); return; }
    if (control?.matches('[data-consent-reject]')) { setConsent(false, false); return; }
    if (control?.matches('[data-consent-save]')) { setConsent(panel.querySelector('[data-consent-analytics]')?.checked === true, panel.querySelector('[data-consent-marketing]')?.checked === true); return; }
    const link = event.target.closest?.('a');
    if (!link) return;
    captureAttribution();
    const card = link.closest('[data-program-card]');
    const section = link.closest('[id]');
    const contact = link.closest('[data-uni-contact]');
    const selectedProgram = contact?.querySelector('[data-contact-program]')?.selectedOptions?.[0]?.dataset.programId;
    let linkMode = '';
    try { linkMode = new URL(link.href).searchParams.get('modalidad') || ''; } catch {}
    const details = {
      program_id: card?.dataset.programId || selectedProgram || contact?.dataset.programId,
      modalidad: contact?.querySelector('[data-contact-modality]')?.value || linkMode,
      cta_id: ctaIdentifier(link, card),
      cta_location: link.closest('header.uni-header') ? 'header' : link.closest('footer') ? 'footer' : link.closest('[data-whatsapp-float]') ? 'floating' : link.closest('[data-uni-contact]') ? 'form' : 'content',
      section_id: section?.id
    };
    if (/^https:\/\/(wa\.me|api\.whatsapp\.com)\//.test(link.href)) track('whatsapp_click', details);
    else if (card) track('select_program', details);
    else if (link.matches('[data-track],.uni-button,[data-program-cta]')) track('click_cta', details);
  });
  function ready() {
    syncConsent();
    const panel = document.querySelector('[data-consent]');
    if (panel && !window.Shopify?.designMode) {
      panel.hidden = hasConsentDecision();
      const status = panel.querySelector('[data-consent-status]');
      if (status) status.hidden = typeof privacy()?.setTrackingConsent === 'function';
    }
  }
  window.UniAnalytics = Object.freeze({ track, permission, leadAttribution, identifier, safeId, safeText, safeURL, captureAttribution, whenPrivacyReady, version: VERSION });
  if (hasPrivacyApi()) finishPrivacyInitialization();
  else {
    captureAttribution();
    privacyReadyTimeout = window.setTimeout(() => finishPrivacyInitialization(new Error('privacy-api-timeout')), 10000);
    if (typeof window.Shopify?.loadFeatures === 'function') {
      try { window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], finishPrivacyInitialization); }
      catch (error) { finishPrivacyInitialization(error); }
    } else finishPrivacyInitialization(new Error('privacy-api-unavailable'));
  }
  document.addEventListener('visitorConsentCollected', () => { const panel = document.querySelector('[data-consent]'); if (panel && hasConsentDecision()) panel.hidden = true; syncConsent(); });
  document.addEventListener('shopify:section:load', viewProgram);
  document.addEventListener('uni:modalitychange', event => {
    const value = event.detail;
    if (value?.userInitiated && value.previousMode !== value.mode) track('select_modality', { program_id: value.id, modalidad: value.mode, modalidad_anterior: value.previousMode, cta_location: 'program' });
  });
  window.addEventListener('pageshow', syncConsent);
})();
